import React from 'react';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { ScrollArea, Spinner, Box, Dialog, Flex, IconButton, Tooltip } from '@radix-ui/themes';

import Sidebar from 'Components/employersSide/Sidebar';
import { logOutIfTokenExpired } from 'Components/helpers';
import { DateUtilsService } from 'shared/utils/date-utils.service';
import { usePaymentHistory } from './payment-history.provider';
import { PaymentHistoryItem, PaymentRecordState } from './payment-history.types';
import { CloseIcon, InfoOutlineIcon } from 'shared/icons';
import { PaymentStatusMarker } from './payment-status-marker';
import './payment-history.scss';

const renderFeatures = (features?: PaymentHistoryItem['features']) => {
   if (!features) {
      return [];
   }

   return features.map((feature) => String(feature));
};

const PENDING_TOOLTIP = "Найближчим часом з вами зв'яжеться менеджер";

const getStateLabel = (state: PaymentRecordState): string => {
   if (state === 'ACTIVATED') {
      return 'Активований';
   }

   if (state === 'DEACTIVATED') {
      return 'Деактивований';
   }

   return 'В процесі';
};

const renderStatusTooltip = (item: PaymentHistoryItem): string => {
   if (item.state === 'PENDING') {
      return PENDING_TOOLTIP;
   }

   return item.status_name || getStateLabel(item.state);
};

const renderStateLabel = (item: PaymentHistoryItem): React.ReactNode => {
   return (
      <Tooltip content={renderStatusTooltip(item)}>
         <Flex align="center" gap="1">
            <InfoOutlineIcon color="#294a9c" />
            <span>{item.status_name || getStateLabel(item.state)}</span>
         </Flex>
      </Tooltip>
   );
};

function PaymentHistoryView() {
   const navigate = useNavigate();
   const { t } = useTranslation('global');
   const paymentHistoryStore = usePaymentHistory();

   React.useEffect(() => {
      const userType = Cookies.get('user_type');
      const tokenKey = Cookies.get('tokenClient');

      if (userType === 'candidate' || !tokenKey) {
         navigate('/auth/employer-login');
         return;
      }

      paymentHistoryStore.loadPaymentHistory({
         onError: (message) => {
            Swal.fire({
               icon: 'error',
               text: message,
               confirmButtonText: t('membershipPlan.close'),
               title: t('employerFavouriteListProfile.addFavFailedTitle'),
            });
         },
         onAuthError: () => {
            navigate('/');
            logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
         },
      });

      window.scrollTo(0, 0);
   }, [navigate, paymentHistoryStore, t]);

   const secondaryColor = Cookies.get('secondaryColor') ?? '#294a9c';
   const selectedPayment = paymentHistoryStore.selectedPayment;
   const selectedFeatures = renderFeatures(selectedPayment?.features);

   return (
      <>
         <div className="container paymentHistory">
            <div className="row">
               <div className="col-lg-3 col-md-3">
                  <Sidebar />
               </div>

               {paymentHistoryStore.loading ? (
                  <div className="col-lg-9 col-md-9 mb-5 d-flex justify-content-center py-5">
                     <Spinner size="2" />
                  </div>
               ) : (
                  <div className="col-lg-9 col-md-9 mb-5">
                     <div className="PHHeader PageHeader">
                        <div className="d-flex">
                           <img src="/Images/employerSide/icon3color.png" alt="" />
                           <h3 className="mx-2">{t('employerPaymentHistory.paymentHistory')}</h3>
                        </div>

                        <Box mt="5">
                           <ScrollArea
                              type="always"
                              scrollbars="vertical"
                              style={{ maxHeight: 'calc(100vh - 340px)' }}
                           >
                              <Box>
                                 <table className="table">
                                    <thead>
                                       <tr className="table-active TrFirst">
                                          <th scope="col p-3">{t('employerPaymentHistory.no')}</th>
                                          <th scope="col p-3">
                                             {t('employerPaymentHistory.planName')}
                                          </th>
                                          <th scope="col p-3">
                                             {t('employerPaymentHistory.amount')}
                                          </th>
                                          <th scope="col p-3">
                                             {t('employerPaymentHistory.status')}
                                          </th>
                                          <th scope="col p-3">
                                             {t('employerPaymentHistory.startDate')}
                                          </th>
                                          <th scope="col p-3">
                                             {t('employerPaymentHistory.endDate')}
                                          </th>
                                          <th scope="col p-3">
                                             {t('employerPaymentHistory.availableServices')}
                                          </th>
                                       </tr>
                                    </thead>

                                    <tbody>
                                       {paymentHistoryStore.currentData.map((item, index) => (
                                          <tr className="TrDefoult" key={item.id}>
                                             <td>{paymentHistoryStore.recordStart + index}</td>
                                             <td>
                                                <PaymentStatusMarker
                                                   item={item}
                                                   onOpen={(selected) => {
                                                      paymentHistoryStore.openPaymentDialog(
                                                         selected
                                                      );
                                                   }}
                                                />
                                             </td>
                                             <td>{item.amount}</td>
                                             <td>{renderStateLabel(item)}</td>
                                             <td>
                                                {item.start_date
                                                   ? DateUtilsService.toAppFormat(item.start_date)
                                                   : ''}
                                             </td>
                                             <td>
                                                {item.end_date
                                                   ? DateUtilsService.toAppFormat(item.end_date)
                                                   : ''}
                                             </td>
                                             <td className="TrActions">
                                                <button
                                                   type="button"
                                                   onClick={() => {
                                                      paymentHistoryStore.openPaymentDialog(item);
                                                   }}
                                                   className="btn btn-primary button1"
                                                >
                                                   <i className="fa fa-eye"></i>
                                                </button>
                                             </td>
                                          </tr>
                                       ))}
                                    </tbody>
                                 </table>
                              </Box>
                           </ScrollArea>
                        </Box>
                     </div>

                     <div className="paymentHistoryPagination">
                        <p className="text-muted empPaginationData">
                           {t('pagination.NoofRecords')} {paymentHistoryStore.recordStart}-
                           {paymentHistoryStore.recordEnd} of {paymentHistoryStore.totalCount}
                        </p>

                        <div className="d-flex justify-content-center empPaginationButton">
                           <button
                              className="navButton1 me-2"
                              disabled={!paymentHistoryStore.canPrev}
                              onClick={() => {
                                 paymentHistoryStore.onPrev();
                              }}
                           >
                              {t('pagination.Prev')}
                           </button>

                           <button
                              className="navButton1"
                              disabled={!paymentHistoryStore.canNext}
                              onClick={() => {
                                 paymentHistoryStore.onNext();
                              }}
                           >
                              {t('pagination.Next')}
                           </button>
                        </div>
                     </div>
                  </div>
               )}
            </div>
         </div>
         <Dialog.Root
            open={paymentHistoryStore.open}
            onOpenChange={(open) => {
               if (!open) {
                  paymentHistoryStore.closePaymentDialog();
               }
            }}
         >
            <Dialog.Content maxWidth="500px">
               {selectedPayment && (
                  <div className="modals">
                     <Flex justify="between" align="center" mb="3">
                        <h1 style={{ color: secondaryColor, margin: 0 }}>
                           {selectedPayment.transaction_id ?? '-'}
                        </h1>
                        <Dialog.Close>
                           <IconButton
                              variant="ghost"
                              color="gray"
                              radius="full"
                              aria-label="Close dialog"
                              style={{ marginLeft: 'auto' }}
                           >
                              <CloseIcon />
                           </IconButton>
                        </Dialog.Close>
                     </Flex>

                     <div className="modalBody mt-4">
                        <div className="row">
                           <div className="col-md-4 fw-bold m-2">
                              {t('employerPaymentHistory.planName')}:{' '}
                           </div>
                           <div className="col-md-6 m-2">{selectedPayment.plan_name}</div>
                        </div>
                        <div className="row">
                           <div className="col-md-4 fw-bold m-2">
                              {t('employerPaymentHistory.amount')}:{' '}
                           </div>
                           <div className="col-md-4 m-2">{selectedPayment.amount}</div>
                        </div>
                        <div className="row">
                           <div className="col-md-4 fw-bold m-2">
                              {t('employerPaymentHistory.transactionId')}:{' '}
                           </div>
                           <div className="col-md-4 m-2">
                              {selectedPayment.transaction_id ?? '-'}
                           </div>
                        </div>
                        <div className="row">
                           <div className="col-md-4 fw-bold m-2">
                              {t('employerPaymentHistory.startDate')}:{' '}
                           </div>
                           <div className="col-md-4 m-2">
                              {selectedPayment.start_date
                                 ? DateUtilsService.toAppFormat(selectedPayment.start_date)
                                 : '-'}
                           </div>
                        </div>
                        <div className="row">
                           <div className="col-md-4 fw-bold m-2">
                              {t('employerPaymentHistory.endDate')}:{' '}
                           </div>
                           <div className="col-md-4 m-2">
                              {selectedPayment.end_date
                                 ? DateUtilsService.toAppFormat(selectedPayment.end_date)
                                 : '-'}
                           </div>
                        </div>
                        <div className="col-md-12 fw-bold m-2">
                           {t('employerPaymentHistory.availableForUse')}:{' '}
                        </div>
                        <div className="col-md-12 m-2">
                           {selectedFeatures.map((feature, index) => (
                              <div key={`${feature}-${index}`}>
                                 {index + 1}. {feature}
                                 <br />
                              </div>
                           ))}
                        </div>
                     </div>
                  </div>
               )}
            </Dialog.Content>
         </Dialog.Root>
      </>
   );
}

export const PaymentHistory = observer(PaymentHistoryView);
